import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import storage from 'utils/storage';
import { STORAGE_KEYS } from 'utils/constants';

const coreAxios = axios.create();

// Request interceptor to add auth token and workspace ID
coreAxios.interceptors.request.use((req) => {
  const accessToken = storage.get(STORAGE_KEYS.ACCESS_TOKEN);
  const workspaceId = storage.get(STORAGE_KEYS.CURRENT_WORKSPACE);

  if (accessToken) {
    req.headers.authorization = `Bearer ${accessToken}`;
  }

  // Add workspace ID header for API requests that need it
  if (workspaceId) {
    req.headers['X-Workspace-ID'] = workspaceId;
  }

  return req;
});

// Response interceptor to handle errors
coreAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear storage and redirect to login
      storage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const axiosBaseQuery =
  (
    { baseUrl } = { baseUrl: import.meta.env.VITE_APP_BASE_URL }
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      const result = await coreAxios({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err?.response?.status,
          data: err?.response?.data || err.message,
        },
      };
    }
  };

export default axiosBaseQuery;

